import { useState, useRef, useEffect, useCallback, useMemo, memo } from "react"
import { useNavigate } from "react-router-dom"
import { PlusCircle, X, User, Search, StickyNote, FileDown, Truck, ArrowDownAZ } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { cn } from "@/lib/utils"
import { Item } from "@/types/purchase"
import { OrderDatePicker } from "@/components/OrderDatePicker"
import { useAuth } from "@/hooks/useAuth"
import { format } from "date-fns"
import { ko } from "date-fns/locale"
import { supabase } from "@/supabase/client" // supabase 클라이언트 직접 import

import { OrderStatus } from "@/components/OrderControls";
import { DepositRowChip } from './deposits/DepositRowChip';
import { DepositJumpBanner } from './deposits/DepositJumpBanner';
import { useDepositJump } from '@/hooks/useDepositJump';
import type { DepositIncomingCard } from '@/supabase/deposit/types/deposit';
import { useDepositIncomingContext } from '@/contexts/DepositIncomingContext';
import type { PaymentMethod } from "@/supabase/order/types/order";
import { BlacklistBadge } from "@/components/BlacklistBadge";
import type { BlacklistEntry } from "@/supabase/blacklist/types/blacklist";

export type CustomerTemplate = {
  id: string
  nickname: string
  template: string
  items: Item[]
  is_keep: boolean
  status: OrderStatus
  shippingFeeOverride?: number | null; // Added for per-customer shipping override
  paymentMethod?: PaymentMethod | null; // 결제수단 (opt-in 기능, null = 미기재)
  memo: string;
  createdAt?: string;
  totalPrice: number;
  order_date: string;
}

interface CustomerTemplateManagerProps {
  activeCustomerId: string | null
  customerTemplates: CustomerTemplate[]
  onCustomerSelect: (customerId: string) => void
  onCustomerAdd: (nickname: string) => Promise<string | undefined> | (string | undefined)
  onCustomerRemove: (customerId: string) => void
  onCustomerEdit: (customerId: string, newNickname: string) => void
  titleStyle?: string
  selectedDate?: Date
  setSelectedDate?: (date: Date) => void
  isDatePickerEnabled?: boolean
  onDateChange?: (date: Date) => void
  onDownloadSettlement: () => void;
  hasPaidOrders: boolean;
  onOpenShippingModal: (customer: CustomerTemplate) => void;
  // 새로 추가: 배송 정보 입력 여부를 알 수 있는 order_id Set
  ordersWithShippingInfo?: Set<number>;
  // 입금매칭 단계2: 고객 그리드 최상단에 끼워 넣을 '입금 들어옴' 파랑 카드 슬롯.
  // 미전달(undefined)이면 그리드는 기존과 100% 동일(behavior-inert).
  incomingSlot?: React.ReactNode;
  // 분할입금 D10 게이트: Home이 계산한 선택 가능 상태 목록(selectableOrderStatuses 산출물).
  // '부분 입금' legend는 이 배열에 포함되거나(계좌 연동) 실제 그런 주문이 있을 때만 노출.
  statusOptions?: OrderStatus[];
  // 거파(블랙리스트) 배지 — 미전달(undefined)이면 그리드는 기존과 100% 동일(incomingSlot 선례).
  // Map은 useBlacklist가 entries 변경 시에만 재생성 → row memo 계약 안전.
  blacklistByNickname?: Map<string, BlacklistEntry>;
}

// 비편집 고객 행의 내부 콘텐츠 — memo로 추출해 customerTemplates 배열이 새로 생겨도
// 변경 안 된 행(customer ref 보존)은 재렌더하지 않게 한다. JSX는 기존과 동일하며
// activeCustomerId 비교를 isActive prop으로 받는다. 배송정보 여부는 Set이 아니라 부모 map에서
// 파생한 hasShippingInfo boolean으로 받는다 — 저장 시 Set 참조 교체가 전 행 memo를 깨지 않도록
// (기존 수정 저장 true→true = 재렌더 0, 신규 저장만 해당 1행 false→true).
const CustomerRow = memo(function CustomerRow({
  customer,
  isActive,
  hasShippingInfo,
  onOpenShippingModal,
  onStartEdit,
  onRemove,
  deposit,
  confirmingDeposit,
  depositJustArrived,
  depositConfirmed,
  onConfirmDeposit,
  onRejectDeposit,
  onUndoDeposit,
  depositInFlightAction,
  blacklistEntry,
}: {
  customer: CustomerTemplate
  isActive: boolean
  hasShippingInfo: boolean
  onOpenShippingModal: (customer: CustomerTemplate) => void
  onStartEdit: (customer: CustomerTemplate, e: React.MouseEvent) => void
  onRemove: (customerId: string) => void
  deposit?: DepositIncomingCard;
  confirmingDeposit?: boolean;
  depositJustArrived?: boolean;
  depositConfirmed?: boolean;
  onConfirmDeposit?: (card: DepositIncomingCard) => void;
  onRejectDeposit?: (card: DepositIncomingCard) => void;
  onUndoDeposit?: (card: DepositIncomingCard) => void;
  depositInFlightAction?: 'confirm' | 'reject' | null;
  blacklistEntry?: BlacklistEntry;
}) {
  return (
    <div className="flex items-center justify-between w-full">
      <div className="flex flex-col items-start gap-2 flex-1 min-w-0">
        <div className="flex items-center w-full">
          <User className={cn(
            "w-3 h-3 mr-1.5 flex-shrink-0",
            isActive
              ? "text-indigo-600"
              : "text-indigo-500 group-hover:text-indigo-600",
          )} />
          <span
            className={cn(
              "flex-1 overflow-hidden text-ellipsis whitespace-nowrap text-xs",
              isActive ? "font-bold text-indigo-700" : "font-medium text-gray-700",
            )}
          >
            {customer.nickname}
          </span>
          {blacklistEntry && <BlacklistBadge entry={blacklistEntry} />}
        </div>
        {deposit && deposit.depositorName && deposit.depositorName !== customer.nickname && (
          <span className="text-[10px] text-blue-500 leading-none">{deposit.depositorName} 입금</span>
        )}
        {customer.status === '입금 완료' && (
          <Button
            size="sm"
            variant="outline"
            className={hasShippingInfo
              ? 'h-6 px-2 text-xs bg-white border-emerald-200 text-emerald-700 hover:bg-emerald-50'
              : 'h-6 px-2 text-xs bg-white/70 hover:bg-white'}
            onClick={(e) => {
              e.stopPropagation();
              onOpenShippingModal(customer);
            }}
          >
            <Truck className="w-3 h-3 mr-1" />
            {hasShippingInfo ? '배송 정보 수정' : '배송 정보 입력'}
          </Button>
        )}
      </div>
      <div className="flex items-center flex-shrink-0 pl-1.5">
        {customer.memo && (
          <StickyNote className={cn(
            "w-3.5 h-3.5",
            // 입금 칩과 같이 뜰 때 아이콘이 pill에 딱 붙어 조잡 — 칩 있을 때만 간격(오너 피드백 2026-07-06)
            deposit && onConfirmDeposit && onRejectDeposit && "mr-2",
            isActive ? "text-amber-500" : "text-amber-400 group-hover:text-amber-500"
          )} />
        )}
        {deposit && onConfirmDeposit && onRejectDeposit ? (
          <DepositRowChip
            card={deposit}
            confirming={!!confirmingDeposit}
            inFlightAction={depositInFlightAction ?? null}
            justArrived={depositJustArrived}
            confirmed={depositConfirmed}
            onConfirm={onConfirmDeposit}
            onReject={onRejectDeposit}
            onUndo={onUndoDeposit}
          />
        ) : (
          <div className={cn(
            "flex items-center opacity-0 group-hover:opacity-100 transition-opacity",
            isActive && "opacity-100"
          )}>
            <Button
              variant="ghost"
              size="icon"
              className={cn(
                "h-5 w-5",
                isActive
                  ? "text-indigo-200 hover:bg-indigo-500 hover:text-white"
                  : "text-gray-400 hover:bg-gray-200 hover:text-gray-700",
              )}
              onClick={(e) => onStartEdit(customer, e)}
              aria-label="닉네임 편집"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path></svg>
            </Button>
            {customer.nickname !== "고객 닉네임" && (
              <Button
                variant="ghost"
                size="icon"
                className={cn(
                  "h-5 w-5",
                  isActive
                    ? "text-red-300 hover:bg-red-500 hover:text-white"
                    : "text-gray-400 hover:bg-gray-200 hover:text-red-600",
                )}
                onClick={(e) => { e.stopPropagation(); onRemove(customer.id); }}
                aria-label="고객 삭제"
              >
                <X className="w-3 h-3" />
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  );
});

function CustomerTemplateManagerImpl({
  activeCustomerId,
  customerTemplates,
  onCustomerSelect,
  onCustomerAdd,
  onCustomerRemove,
  onCustomerEdit,
  titleStyle = "text-lg font-semibold text-[#333d4b]",
  selectedDate = new Date(),
  setSelectedDate,
  isDatePickerEnabled = false,
  onDateChange,
  onDownloadSettlement,
  hasPaidOrders,
  onOpenShippingModal,
  ordersWithShippingInfo, // 새로 추가
  incomingSlot, // 입금매칭 단계2: 그리드 최상단 파랑 카드 슬롯(optional)
  statusOptions, // 분할입금 D10 게이트: '부분 입금' legend 노출 판정(optional)
  blacklistByNickname, // 거파 배지: 닉네임 → 블랙리스트 엔트리 Map(optional)
}: CustomerTemplateManagerProps) {
  const [newNickname, setNewNickname] = useState("")
  const [isAdding, setIsAdding] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [searchQuery, setSearchQuery] = useState("")
  const [sortByNickname, setSortByNickname] = useState(false)
  const [editingCustomerId, setEditingCustomerId] = useState<string | null>(null)
  const [editNickname, setEditNickname] = useState("")
  const inputRef = useRef<HTMLInputElement>(null)
  const editInputRef = useRef<HTMLInputElement>(null)
  const listContainerRef = useRef<HTMLDivElement>(null)
  const { user } = useAuth() // supabase 제거
  const { depositByOrderId, confirm: confirmDeposit, reject: rejectDeposit, undo: undoDeposit, confirming: confirmingDeposit, pendingMatchId, pendingAction: depositPendingAction, justArrivedMatchIds, confirmedMatchIds } = useDepositIncomingContext();
  const navigate = useNavigate()
  const [suggestions, setSuggestions] = useState<string[]>([])
  const [isSearching, setIsSearching] = useState(false)
  const [showSuggestions, setShowSuggestions] = useState(false)
  const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null)

  const debouncedSearch = (query: string) => {
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current)
    }
    if (!query.trim()) {
      setSuggestions([])
      setShowSuggestions(false)
      return
    }
    debounceTimeoutRef.current = setTimeout(async () => {
      if (!supabase) return;
      setIsSearching(true)
      setShowSuggestions(true)
      try {
        const { data, error } = await supabase.functions.invoke('search-customers', {
          body: { q: query },
        })

        if (error) throw error

        const suggestionsArray = Array.isArray(data) ? data : (data?.nicknames || []);

        const filteredSuggestions = suggestionsArray.filter((s: string) => s.toLowerCase() !== query.toLowerCase());
        setSuggestions(filteredSuggestions);

      } catch (error) {
        console.error('Error searching customers:', error)
        setSuggestions([])
      } finally {
        setIsSearching(false)
      }
    }, 150)
  }

  const handleSuggestionClick = (suggestion: string, type: 'new' | 'edit') => {
    if (type === 'new') {
      setNewNickname(suggestion);
    } else {
      setEditNickname(suggestion);
    }
    setSuggestions([]);
    setShowSuggestions(false);
  };

  // Handle adding a new customer
  const handleAddCustomer = async () => {
    if (newNickname.trim() && !isSubmitting) {
      setIsSubmitting(true);
      try {
        const addedId = await onCustomerAdd(newNickname.trim());
        // 스크롤 이동: 목록 컨테이너 내에서 방금 추가된 항목으로 스크롤
        setTimeout(() => {
          const container = listContainerRef.current;
          if (!container) return;
          // activeCustomerId가 즉시 반영되지 않을 수 있으므로 data-customer-id로 직접 찾기
          if (addedId) {
            const target = container.querySelector(`[data-customer-id="${addedId}"]`) as HTMLElement | null;
            if (target) {
              target.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
            }
          }
        }, 0);
        setNewNickname("")
        setIsAdding(false)
        setShowSuggestions(false)
      } catch (error) {
        // 에러는 Home.tsx에서 처리하므로 여기서는 별도 처리 안함
      } finally {
        setIsSubmitting(false);
      }
    }
  }

  // 날짜 변경 핸들러
  const handleDateChange = (date: Date) => {
    if (setSelectedDate) {
      setSelectedDate(date);
    }
    if (onDateChange) {
      onDateChange(date);
    }
    const formattedDate = format(date, 'yyyy-MM-dd');
    navigate(`?date=${formattedDate}`, { replace: true });
  };

  // Focus input when adding mode is enabled
  useEffect(() => {
    if (isAdding && inputRef.current) {
      inputRef.current.focus()
    }
  }, [isAdding])

  // 고객 수정 모드 시작
  const startEditing = useCallback((customer: CustomerTemplate, e: React.MouseEvent) => {
    e.stopPropagation()
    setEditingCustomerId(customer.id)
    setEditNickname(customer.nickname)
  }, [])

  // 고객 닉네임 수정 완료
  const handleEditCustomer = () => {
    if (editingCustomerId && editNickname.trim()) {
      onCustomerEdit(editingCustomerId, editNickname.trim())
      setEditingCustomerId(null)
      setEditNickname("")
      setShowSuggestions(false)
    }
  }

  // 수정 모드 취소
  const cancelEditing = () => {
    setEditingCustomerId(null)
    setEditNickname("")
    setShowSuggestions(false)
  }

  // 수정 입력 필드에 포커스
  useEffect(() => {
    if (editingCustomerId && editInputRef.current) {
      editInputRef.current.focus()
    }
  }, [editingCustomerId])

  // 검색어에 따라 고객 필터링 및 '고객 닉네임'이 있으면 0번째로 정렬.
  // ★useMemo로 안정화(codexloop P2 R1): 매 렌더 새 배열이면 useDepositJump의 IO effect가
  //   매 렌더 재실행 → 렌더↔effect 피드백 루프. 입력 동일하면 같은 참조 유지.
  const filteredCustomers = useMemo(() => customerTemplates
    .filter(customer => customer.nickname.toLowerCase().includes(searchQuery.toLowerCase()))
    .sort((a, b) => {
      // '고객 닉네임'을 가진 고객이 있으면 항상 최상단에 표시
      if (a.nickname === "고객 닉네임") return -1;
      if (b.nickname === "고객 닉네임") return 1;
      if (sortByNickname) {
        return a.nickname.localeCompare(b.nickname, 'ko', { sensitivity: 'base' });
      }
      return 0;
    }), [customerTemplates, searchQuery, sortByNickname]);

  // 화면 밖/검색가림 매칭 행을 점프 배너로 surface(놓침 방지). 로직은 훅으로 격리(Anti-Bloat).
  const { hiddenDepositCount, jumpToHiddenDeposit } = useDepositJump(
    depositByOrderId, filteredCustomers, listContainerRef, searchQuery, setSearchQuery,
  );

  return (
    // Removed outer white card styling, as it's now part of the main Card in Home.tsx
    <>
      {/* --- 상단 컨트롤 영역 --- */}
      {/* --- 상단 컨트롤 영역 --- */}
      <div className="border-b pb-4 mb-4">
        <div className="flex flex-col items-center text-center mb-3 gap-3">
          <div className="text-center">
            <h2 className={titleStyle}>고객별 상품 관리</h2>
            <p className="text-sm text-gray-500 mt-2 leading-relaxed">
              {user && isDatePickerEnabled
                ? `${format(selectedDate, 'yyyy년 MM월 dd일', { locale: ko })} 주문 내역을 고객별로 관리합니다.`
                : "고객별로 상품 목록을 관리합니다."}
            </p>
          </div>
          {user && isDatePickerEnabled && (
            <div className="flex flex-col items-center gap-2">
              <OrderDatePicker
                date={selectedDate}
                setDate={handleDateChange}
                onDateChange={handleDateChange}
                className="flex-shrink-0"
              />
              <Button
                variant="outline"
                onClick={onDownloadSettlement}
                disabled={!hasPaidOrders}
                className="bg-teal-500 hover:bg-teal-600 text-white"
              >
                <FileDown className="mr-2 h-4 w-4" />
                입금완료 정산서 목록 엑셀 다운로드
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* --- 검색 영역 --- */}
      <div className="mb-4 flex items-center gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3.5 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
          <Input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="고객 닉네임 검색..."
            className="pl-10 pr-4 py-2.5 text-sm h-9"
          />
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setSortByNickname(prev => !prev)}
          aria-label={sortByNickname ? "가나다순 정렬 해제" : "가나다순 정렬"}
          aria-pressed={sortByNickname}
          className={cn(
            "h-9 w-9 p-0 flex-shrink-0",
            sortByNickname
              ? "text-indigo-600 bg-indigo-50 hover:bg-indigo-100"
              : "text-gray-400 hover:text-gray-600"
          )}
          title={sortByNickname ? "주문 순서로 보기" : "가나다 순으로 보기"}
        >
          <ArrowDownAZ className="h-4 w-4" />
        </Button>
      </div>

      {/* --- 상태 범례 --- */}
      <div className="mb-4 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-gray-600">
        <span className="font-medium">주문상태:</span>
        <div className="flex items-center">
          <span className="w-3 h-3 rounded-full bg-blue-100 border border-blue-400 mr-1.5"></span>
          <span className="text-blue-700 font-medium">입금 들어옴</span>
        </div>
        <div className="flex items-center">
          <span className="w-3 h-3 rounded-full bg-red-100 border border-red-300 mr-1.5"></span>
          <span>킵</span>
        </div>
        {(statusOptions?.includes('부분 입금') || customerTemplates.some((c) => c.status === '부분 입금')) && (
          <div className="flex items-center">
            <span className="w-3 h-3 rounded-full bg-amber-100 border border-amber-300 mr-1.5"></span>
            <span>부분 입금</span>
          </div>
        )}
        <div className="flex items-center">
          <span className="w-3 h-3 rounded-full bg-yellow-100 border border-yellow-300 mr-1.5"></span>
          <span>입금 완료</span>
        </div>
        <div className="flex items-center">
          <span className="w-3 h-3 rounded-full bg-green-100 border border-green-300 mr-1.5"></span>
          <span>출고 완료</span>
        </div>
        <div className="flex items-center">
          <span className="w-3 h-3 rounded-full bg-white border border-gray-300 mr-1.5"></span>
          <span>미지정</span>
        </div>
      </div>

      {/* 점프 배너 — 스크롤 root '밖'에 둔다(codexloop P2 R1 #3): root 안에 sticky로 두면
          IO가 배너에 가린 행을 '보임'으로 오판한다. 안 보이는 매칭 있을 때만 렌더(없으면 null). */}
      <DepositJumpBanner offscreenCount={hiddenDepositCount} onJump={jumpToHiddenDeposit} />

      {/* --- 고객 목록 그리드 (스크롤 적용) --- */}
      <div className="overflow-y-auto max-h-[18rem] md:max-h-[21rem] lg:max-h-[24rem] p-1" ref={listContainerRef}>
        <div className="grid grid-cols-1 gap-3">
          {/* 입금매칭 단계2: '입금 들어옴' 파랑 카드(있을 때만, 없으면 null=무변화) */}
          {incomingSlot}
          {filteredCustomers.map((customer) => {
            const m = customer.id.match(/^order_(\d+)$/);
            const orderId = m ? Number(m[1]) : null;
            const deposit = orderId != null ? depositByOrderId.get(orderId) : undefined;
            const rowBlacklistEntry = blacklistByNickname?.get(customer.nickname.trim());
            // 행에는 Set 대신 행별 primitive를 파생해 전달 — Set 참조 교체가 memo를 깨지 않게.
            // 계산 규칙은 기존 CustomerRow 내부와 동일(!!(orderId && has(orderId))), 위치 이동만.
            const rowHasShippingInfo = !!(orderId && ordersWithShippingInfo?.has(orderId));
            return (
            <div
              key={customer.id}
              data-customer-id={customer.id}
              className={cn(
                "relative group px-1.5 py-1 rounded-md border cursor-pointer transition-all duration-150 ease-in-out flex flex-col justify-center",
                // 다이얼로그 오픈 시 Radix의 body/root 변이가 전 문서 스타일 recalc를 유발 — 행 수 종속
                // recalc +135ms@6x/120행 실측(CDP Performance 귀속). 오프스크린 행의 style/layout/paint를
                // 스킵해 이 비용을 차단. paint containment가 행 밖 렌더를 클리핑하므로 행 밖으로 나가는
                // 요소를 가진 행은 제외: 편집 중 행(자동완성 드롭다운 top-full) + 블랙리스트 행
                // (BlacklistBadge 툴팁이 무포털 인라인 — ui/tooltip.tsx에 Portal 없음, fixed 포지셔닝도
                // contain:paint 조상이 containing block이 되어 깨짐).
                // intrinsic-size 힌트는 행 높이 2부류에 맞춤(배송 버튼 有 박스 58px / 無 30px 실측,
                // 힌트는 content-box 기준이라 py-1+border 10px 차감 = 48/20px) — 단일 58px 힌트는 혼합
                // 상태 120행에서 scrollHeight 24% 드리프트 실측. auto가 렌더 후 실높이를 기억하므로
                // 힌트는 미렌더 행에만 쓰임 = 근사면 충분. 입금매칭 칩(DepositRowChip, min-h 32px 버튼)이
                // 달린 행도 키가 커지므로 48px 부류로.
                editingCustomerId !== customer.id && !rowBlacklistEntry && (customer.status === '입금 완료' || deposit
                  ? "[content-visibility:auto] [contain-intrinsic-size:auto_48px]"
                  : "[content-visibility:auto] [contain-intrinsic-size:auto_20px]"),
                {
                  "bg-red-100 border-red-300 hover:bg-red-200": customer.is_keep,
                  "bg-amber-100 border-amber-300 hover:bg-amber-200": !customer.is_keep && customer.status === '부분 입금',
                  "bg-yellow-100 border-yellow-300 hover:bg-yellow-200": !customer.is_keep && customer.status === '입금 완료',
                  "bg-green-100 border-green-300 hover:bg-green-200": !customer.is_keep && customer.status === '출고 완료',
                  "bg-blue-50 border-blue-300 border-l-4 border-l-blue-400": !customer.is_keep && customer.status === '미지정' && deposit,
                  "bg-white text-gray-700 border-gray-300 hover:border-indigo-400 hover:shadow-sm": !customer.is_keep && customer.status === '미지정' && !deposit,
                },
                activeCustomerId === customer.id && "border-indigo-500 border-2 shadow-lg",
                // 거파 스트라이프 — 상태 배경색(킵=red-100 포함)과 독립 채널이라 의미 충돌 없음.
                // 입금 파랑 스트라이프(border-l-blue-400)와 겹치면 twMerge 후행 승리로 빨강 우선(위험 > 정보).
                rowBlacklistEntry && "border-l-4 border-l-red-600"
              )}
              onClick={() => editingCustomerId !== customer.id && onCustomerSelect(customer.id)}
            >
              {editingCustomerId === customer.id ? (
                <div className="relative flex flex-col gap-2">
                  <Input
                    ref={editInputRef}
                    value={editNickname}
                    onChange={(e) => {
                      setEditNickname(e.target.value)
                      debouncedSearch(e.target.value)
                    }}
                    onBlur={() => setTimeout(() => setShowSuggestions(false), 150)}
                    className={cn(
                      "h-6 text-xs",
                      activeCustomerId === customer.id
                        ? "bg-white text-gray-800 border-indigo-300 focus:ring-indigo-500"
                        : "border-gray-300",
                    )}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") handleEditCustomer()
                      if (e.key === "Escape") cancelEditing()
                    }}
                    onClick={(e) => e.stopPropagation()}
                    autoFocus
                  />
                  {showSuggestions && editingCustomerId === customer.id && suggestions.length > 0 && (
                    <div className="absolute top-full left-0 z-20 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-40 overflow-y-auto">
                      {isSearching ? (
                        <div className="p-2 text-xs text-gray-500">검색 중...</div>
                      ) : (
                        suggestions.map((suggestion, index) => (
                          <div
                            key={index}
                            className="px-3 py-1.5 text-xs cursor-pointer hover:bg-gray-100"
                            onMouseDown={(e) => {
                              e.preventDefault();
                              handleSuggestionClick(suggestion, 'edit');
                            }}
                          >
                            {suggestion}
                          </div>
                        ))
                      )}
                    </div>
                  )}
                  <div className="flex justify-end space-x-1.5">
                    <Button
                      size="icon"
                      variant="ghost"
                      className={cn(
                        "h-5 w-5",
                        activeCustomerId === customer.id
                          ? "text-green-300 hover:bg-green-400 hover:text-white"
                          : "text-green-500 hover:bg-green-100",
                      )}
                      onClick={(e) => { e.stopPropagation(); handleEditCustomer(); }}
                      aria-label="닉네임 저장"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>
                    </Button>
                    <Button
                      size="icon"
                      variant="ghost"
                      className={cn(
                        "h-5 w-5",
                        activeCustomerId === customer.id
                          ? "text-gray-300 hover:bg-gray-400 hover:text-white"
                          : "text-gray-400 hover:bg-gray-100",
                      )}
                      onClick={(e) => { e.stopPropagation(); cancelEditing(); }}
                      aria-label="편집 취소"
                    >
                      <X className="w-3 h-3" />
                    </Button>
                  </div>
                </div>
              ) : (
                <CustomerRow
                  customer={customer}
                  isActive={activeCustomerId === customer.id}
                  hasShippingInfo={rowHasShippingInfo}
                  onOpenShippingModal={onOpenShippingModal}
                  onStartEdit={startEditing}
                  onRemove={onCustomerRemove}
                  deposit={deposit}
                  onRejectDeposit={rejectDeposit}
                  depositInFlightAction={deposit && pendingMatchId === deposit.matchId ? depositPendingAction : null}
                  confirmingDeposit={deposit ? confirmingDeposit : false}
                  depositJustArrived={deposit ? justArrivedMatchIds.has(deposit.matchId) : false}
                  depositConfirmed={deposit ? confirmedMatchIds.has(deposit.matchId) : false}
                  onConfirmDeposit={confirmDeposit}
                  onUndoDeposit={undoDeposit}
                  blacklistEntry={rowBlacklistEntry}
                />
              )}
            </div>
            );
          })}
        </div>
      </div>

      {/* --- 하단 추가 영역 --- */}
      <div className="mt-4 pt-4 border-t">
        {isAdding ? (
          <div className="flex justify-center">
            <div className="relative w-64 flex flex-col gap-2">
              <Input
                ref={inputRef}
                value={newNickname}
                onChange={(e) => {
                  setNewNickname(e.target.value)
                  debouncedSearch(e.target.value)
                }}
                onBlur={() => setTimeout(() => setShowSuggestions(false), 150)}
                placeholder="새 고객 닉네임"
                className="h-9 text-sm border-indigo-300 focus:ring-indigo-500"
                data-tour="nickname-input"
                onKeyDown={(e) => {
                  if (e.key === "Enter" && newNickname.trim()) handleAddCustomer()
                  if (e.key === "Escape") {
                    setIsAdding(false)
                    setShowSuggestions(false)
                  }
                }}
                autoFocus
              />
              {showSuggestions && isAdding && suggestions.length > 0 && (
                <div className="absolute bottom-full left-0 z-20 w-full mb-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-40 overflow-y-auto">
                  {isSearching ? (
                    <div className="p-2 text-xs text-gray-500">검색 중...</div>
                  ) : (
                    suggestions.map((suggestion, index) => (
                      <div
                        key={index}
                        className="px-3 py-1.5 text-xs cursor-pointer hover:bg-gray-100"
                        onMouseDown={(e) => {
                          e.preventDefault();
                          handleSuggestionClick(suggestion, 'new');
                        }}
                      >
                        {suggestion}
                      </div>
                    ))
                  )}
                </div>
              )}
              <div className="flex justify-end space-x-2">
                <Button size="sm" variant="ghost" onClick={() => setIsAdding(false)} className="text-gray-600 hover:bg-gray-200">
                  취소
                </Button>
                <Button size="sm" onClick={handleAddCustomer} disabled={!newNickname.trim() || isSubmitting} className="bg-indigo-600 hover:bg-indigo-700 text-white">
                  추가
                </Button>
              </div>
            </div>
          </div>
        ) : (
          <div className="flex justify-center">
            <button
              id="add-nickname-button"
              className="relative flex items-center justify-center h-10 px-4 py-2 rounded-md text-indigo-600 hover:bg-indigo-50 transition-all duration-150 ease-in-out focus:outline-none group"
              onClick={() => setIsAdding(true)}
              data-tour="nickname-input"
            >
              <svg className="absolute inset-0 w-full h-full" width="100%" height="100%" xmlns="http://www.w3.org/2000/svg">
                <rect
                  x="1" y="1"
                  width="calc(100% - 2px)" height="calc(100% - 2px)"
                  rx="5"
                  stroke="currentColor"
                  strokeWidth="2"
                  fill="none"
                  strokeDasharray="4 4"
                  className="animate-dash-animation group-hover:stroke-indigo-700"
                />
              </svg>
              <div className="relative z-10 flex items-center justify-center">
                <PlusCircle className="w-4 h-4 mr-2" />
                <span className="text-sm font-medium">다른 닉네임 추가</span>
              </div>
            </button>
          </div>
        )}
      </div>
    </>
  )
}

export const CustomerTemplateManager = memo(CustomerTemplateManagerImpl);
